import React, { useState, useEffect, useRef } from 'react';

/**
 * BRANDING & SEO CONSTANTS
 */
const BRAND = {
  name: "Agenzyai",
  email: "info@agenzyai.com",
  whatsapp: "+92 339 9998509",
  logoUrl: "agenzy.png", 
  logoHeight: "110px", 
  seo: {
    title: "Agenzyai | Autonomous AI Agents & Marketing",
    description: "Deploying autonomous AI agents and intelligent marketing to scale your growth on autopilot.",
    keywords: "AI Automation, Multi-Agent Systems, VAPI Voice Agents, Digital Marketing AI, Agenzyai, Business Automation"
  },
  colors: {
    violet: '#8B5CF6',
    purple: '#6D28D9',
    blue: '#3B82F6',
    dark: '#0F172A',
    bg: '#ffffff',
    slate50: '#F8FAFC',
    slate100: '#F1F5F9',
    slate200: '#E2E8F0',
    slate400: '#94A3B8',
    slate500: '#64748B',
    slate700: '#334155',
    slate900: '#0F172A',
    grid: 'rgba(109, 40, 217, 0.15)' 
  }
};

// --- Custom CSS ---
const customStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lobster&family=Roboto+Condensed:wght@300;400;700&display=swap');

  :root {
    --cursor-x: 0px;
    --cursor-y: 0px;
    --follower-x: 0px;
    --follower-y: 0px;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Roboto Condensed', sans-serif;
    background-color: ${BRAND.colors.bg};
    color: ${BRAND.colors.slate900};
    overflow-x: hidden;
    cursor: none; /* Hide default cursor */
  }

  a, button, select, input, textarea {
    cursor: none !important;
  }

  .font-lobster { font-family: 'Lobster', cursive; }
  .font-roboto { font-family: 'Roboto Condensed', sans-serif; }
  
  /* --- MAGNETIC CURSOR --- */
  .custom-cursor {
    position: fixed;
    width: 8px;
    height: 8px;
    background: #000;
    border-radius: 50%;
    pointer-events: none;
    z-index: 10000;
    transform: translate3d(var(--cursor-x), var(--cursor-y), 0);
    transition: transform 0.05s linear;
  }

  .cursor-follower {
    position: fixed;
    width: 40px;
    height: 40px;
    border: 1.5px solid ${BRAND.colors.violet};
    border-radius: 50%;
    pointer-events: none;
    z-index: 9999;
    transform: translate3d(calc(var(--follower-x) - 16px), calc(var(--follower-y) - 16px), 0);
    transition: transform 0.15s ease-out;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* --- DARK GLOW EFFECT --- */
  .cursor-glow {
    position: fixed;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(15, 23, 42, 0.07) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
    z-index: 1;
    transform: translate3d(calc(var(--cursor-x) - 300px), calc(var(--cursor-y) - 300px), 0);
    transition: transform 0.1s ease-out;
  }

  /* --- FLUID NEON BACKGROUND --- */
  .bg-canvas {
    position: fixed;
    inset: 0;
    z-index: -1;
    background: #fff;
    overflow: hidden;
  }

  .aura-blob {
    position: absolute;
    width: 50vw;
    height: 50vw;
    background: radial-gradient(circle, ${BRAND.colors.violet}22 0%, transparent 70%);
    filter: blur(80px);
    border-radius: 50%;
    animation: moveAura 20s infinite alternate;
  }

  .aura-blob-2 {
    background: radial-gradient(circle, ${BRAND.colors.blue}11 0%, transparent 70%);
    width: 60vw;
    height: 60vw;
    right: -10vw;
    top: -10vw;
    animation: moveAura 25s infinite alternate-reverse;
  }

  @keyframes moveAura {
    0% { transform: translate(-10%, -10%) scale(1); }
    100% { transform: translate(20%, 20%) scale(1.2); }
  }

  .grid-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: linear-gradient(${BRAND.colors.grid} 1px, transparent 1px), 
                      linear-gradient(90deg, ${BRAND.colors.grid} 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(circle at var(--cursor-x) var(--cursor-y), black, transparent 80%);
  }

  /* --- MAGNETIC CARDS --- */
  .glass-card {
    position: relative;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(15px);
    border: 1px solid ${BRAND.colors.slate200};
    border-radius: 2rem;
    padding: 2rem;
    transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform-style: preserve-3d;
    perspective: 1000px;
  }

  .glass-card:hover {
    box-shadow: 0 40px 80px rgba(139, 92, 246, 0.15);
    transform: translateY(-12px) rotateX(4deg) rotateY(-4deg) scale(1.03);
    border-color: ${BRAND.colors.violet};
    background: rgba(255, 255, 255, 0.9);
  }

  .glass-card::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(139, 92, 246, 0.05) 50%, transparent 100%);
    transform: translateX(-100%);
    transition: transform 0.8s ease;
  }

  .glass-card:hover::after {
    transform: translateX(100%);
  }

  /* --- WHATSAPP ANIMATION --- */
  .whatsapp-container {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    z-index: 1000;
    display: flex;
    align-items: center;
    text-decoration: none;
  }

  .whatsapp-label {
    background: rgba(15, 23, 42, 0.95);
    backdrop-filter: blur(10px);
    padding: 12px 24px;
    border-radius: 100px;
    font-size: 14px;
    font-weight: 700;
    color: white;
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
    margin-right: 15px;
    opacity: 0;
    transform: translateX(20px) scale(0.8);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    pointer-events: none;
    white-space: nowrap;
  }

  .whatsapp-container:hover .whatsapp-label {
    opacity: 1;
    transform: translateX(0) scale(1);
  }

  .whatsapp-btn {
    background: #25D366;
    color: white;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 25px rgba(37, 211, 102, 0.4);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  .whatsapp-container:hover .whatsapp-btn {
    transform: scale(1.1) rotate(12deg);
  }

  .reveal-section {
    opacity: 0;
    transform: translateY(40px);
    transition: all 1s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .reveal-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .submit-btn {
    background: ${BRAND.colors.slate900};
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
  }

  .submit-btn:hover {
    background: ${BRAND.colors.violet};
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 15px 30px rgba(139, 92, 246, 0.3);
  }

  /* Navigation Underline Effect */
  .nav-btn {
    position: relative;
    background: none;
    border: none;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: ${BRAND.colors.slate900};
    transition: color 0.3s;
    padding: 5px 0;
  }
  .nav-btn::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0%;
    height: 2px;
    background: ${BRAND.colors.violet};
    transition: width 0.3s ease;
  }
  .nav-btn:hover::after, .nav-btn.active::after {
    width: 100%;
  }
  .nav-btn.active { color: ${BRAND.colors.violet}; }
`;

const Reveal = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal-section ${isVisible ? 'visible' : ''}`}>
      {children}
    </div>
  );
};

const MagneticCursor = () => {
  useEffect(() => {
    const moveCursor = (e) => {
      document.documentElement.style.setProperty('--cursor-x', `${e.clientX}px`);
      document.documentElement.style.setProperty('--cursor-y', `${e.clientY}px`);
      
      // Delay for follower to create "magnetic" lag
      setTimeout(() => {
        document.documentElement.style.setProperty('--follower-x', `${e.clientX}px`);
        document.documentElement.style.setProperty('--follower-y', `${e.clientY}px`);
      }, 50);
    };
    window.addEventListener('mousemove', moveCursor);
    return () => window.removeEventListener('mousemove', moveCursor);
  }, []);

  return (
    <>
      <div className="custom-cursor" />
      <div className="cursor-follower" />
      <div className="cursor-glow" />
    </>
  );
};

const HeaderLogo = ({ isFooter = false, floating = false }) => (
  <div style={{ display: 'flex', alignItems: 'center' }} className={floating ? "float-element" : ""}>
    <img 
      src={BRAND.logoUrl} 
      alt={`${BRAND.name} Official Logo`}
      style={{ 
        height: isFooter ? '100px' : BRAND.logoHeight, 
        maxHeight: '120px', 
        width: 'auto',
        objectFit: 'contain'
      }}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  </div>
);

const SectionHeader = ({ subtitle, title, description }) => (
  <header style={{ marginBottom: '4rem', textAlign: 'center', padding: '0 1rem', position: 'relative', zIndex: 10 }}>
    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.4em', color: BRAND.colors.purple, marginBottom: '1rem', display: 'block' }}>
      {subtitle}
    </span>
    <h2 className="font-lobster" style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', color: BRAND.colors.slate900, marginBottom: '1.5rem', lineHeight: 1.1 }}>
      {title}
    </h2>
    {description && (
      <p style={{ color: BRAND.colors.slate500, maxWidth: '35rem', margin: '0 auto', fontSize: '1.1rem', lineHeight: 1.7, fontWeight: 300 }}>
        {description}
      </p>
    )}
  </header>
);

const ContactSection = () => {
  const [status, setStatus] = useState(null); 

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    const form = e.target;
    const data = new FormData(form);

    try {
      const response = await fetch("https://formspree.io/f/xvzdzqdp", {
        method: "POST",
        body: data,
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="glass-card" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center', padding: '5rem 2rem' }}>
        <div style={{ fontSize: '5rem', marginBottom: '2rem' }}>✨</div>
        <h3 className="font-lobster" style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>Success!</h3>
        <p style={{ color: BRAND.colors.slate500, marginBottom: '2.5rem', fontSize: '1.1rem' }}>
          One of our AI architects will contact you shortly.
        </p>
        <button onClick={() => setStatus(null)} className="submit-btn" style={{ width: 'auto', margin: '0 auto' }}>Send Another</button>
      </div>
    );
  }

  return (
    <div className="glass-card" style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem' }}>
      <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 'bold', textTransform: 'uppercase', color: BRAND.colors.slate500, marginBottom: '0.8rem', display: 'block' }}>Full Name</label>
            <input type="text" name="name" className="contact-input" style={{ width: '100%', background: BRAND.colors.slate50, border: '1px solid #E2E8F0', padding: '1.2rem', borderRadius: '1rem', outline: 'none' }} placeholder="John Doe" required />
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 'bold', textTransform: 'uppercase', color: BRAND.colors.slate500, marginBottom: '0.8rem', display: 'block' }}>Email</label>
            <input type="email" name="email" className="contact-input" style={{ width: '100%', background: BRAND.colors.slate50, border: '1px solid #E2E8F0', padding: '1.2rem', borderRadius: '1rem', outline: 'none' }} placeholder="john@company.com" required />
          </div>
        </div>
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold', textTransform: 'uppercase', color: BRAND.colors.slate500, marginBottom: '0.8rem', display: 'block' }}>Service Requirement</label>
          <select name="subject" className="contact-input" style={{ width: '100%', background: BRAND.colors.slate50, border: '1px solid #E2E8F0', padding: '1.2rem', borderRadius: '1rem', outline: 'none', appearance: 'none' }} required>
            <option value="">Select a service...</option>
            <option value="Autonomous Multi-Agent Systems">Autonomous Multi-Agent Systems</option>
            <option value="VAPI Voice Agents">VAPI Voice Agents</option>
            <option value="AI Marketing Scaling">Marketing Scaling</option>
          </select>
        </div>
        <div style={{ marginBottom: '2.5rem' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold', textTransform: 'uppercase', color: BRAND.colors.slate500, marginBottom: '0.8rem', display: 'block' }}>Project Context</label>
          <textarea name="message" className="contact-input" style={{ width: '100%', background: BRAND.colors.slate50, border: '1px solid #E2E8F0', padding: '1.2rem', borderRadius: '1rem', outline: 'none', minHeight: '150px' }} placeholder="Tell us about your automation goals..." required></textarea>
        </div>
        <button type="submit" className="submit-btn" disabled={status === 'loading'}>
          {status === 'loading' ? 'Transmitting...' : 'Initiate Inquiry →'}
        </button>
      </form>
    </div>
  );
};

const HomePage = ({ onNavigate }) => (
  <article style={{ position: 'relative', zIndex: 10 }}>
    <section style={{ padding: '6rem 1.25rem 8rem', maxWidth: '1300px', margin: '0 auto', textAlign: 'center' }}>
      <Reveal>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(5px)', border: `1px solid ${BRAND.colors.slate200}`, padding: '0.6rem 1.2rem', borderRadius: '100px', marginBottom: '3rem' }}>
          <span style={{ color: BRAND.colors.violet, fontWeight: 'bold' }}>✦</span>
          <span style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.15em', color: BRAND.colors.slate700 }}>Next-Gen Multi-Agent Orchestration</span>
        </div>
      </Reveal>

      <Reveal>
        <h1 className="font-lobster" style={{ fontSize: 'clamp(3rem, 10vw, 7.5rem)', lineHeight: '1', marginBottom: '2rem', letterSpacing: '-0.02em' }}>
          Scale <span style={{ color: BRAND.colors.violet }}>Smarter.</span> <br />
          Automate <span style={{ color: BRAND.colors.blue }}>Infinite.</span>
        </h1>
      </Reveal>

      <Reveal>
        <p style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)', color: BRAND.colors.slate500, maxWidth: '50rem', margin: '0 auto 4rem', lineHeight: 1.6, fontWeight: 300 }}>
          Deploying autonomous AI agents and intelligent marketing architectures to scale your revenue on complete autopilot.
        </p>
      </Reveal>

      <Reveal>
        <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={() => onNavigate('contact')}
            style={{ background: BRAND.colors.slate900, color: 'white', padding: '1.2rem 3rem', borderRadius: '1rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.15em', border: 'none', cursor: 'pointer', fontSize: '15px' }}>
            Book Architect →
          </button>
          <button 
            onClick={() => onNavigate('automation')}
            style={{ background: 'rgba(255,255,255,0.9)', border: `1px solid ${BRAND.colors.slate200}`, padding: '1.2rem 3rem', borderRadius: '1rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', fontSize: '15px' }}>
            Browse Agents
          </button>
        </div>
      </Reveal>
    </section>
  </article>
);

const AutomationPage = () => {
  const services = [
    { label: "SYSTEMS", title: "Autonomous Multi-Agent Systems", desc: "Complex problem-solving networks that reason, execute, and self-correct using advanced logic chains.", features: ["Self-Healing", "Tool Usage", "Chain-of-Thought"], tech: "LangGraph / CrewAI" },
    { label: "VOICE", title: "VAPI - Neural Voice Agents", desc: "Bilingual, low-latency voice AI capable of handling inbound support and outbound high-ticket sales.", features: ["<500ms Latency", "High EQ", "Global Reach"], tech: "VAPI / ElevenLabs" },
    { label: "DATA", title: "Enterprise Knowledge Base", desc: "Private RAG systems that empower your AI with secure access to your entire business documentation.", features: ["Vector Indexing", "Real-time Sync", "SOC2 Compliance"], tech: "Pinecone / Weaviate" },
    { label: "INTEGRATION", title: "Cross-Platform Workflows", desc: "The glue between your AI brain and your existing tools like CRM, ERP, and communication hubs.", features: ["Custom Nodes", "API Orchestration", "Error Logic"], tech: "n8n / Make / Custom" }
  ];

  return (
    <div style={{ padding: '4rem 1.25rem 6rem', maxWidth: '1200px', margin: '0 auto' }}>
      <Reveal>
        <SectionHeader subtitle="AI Automation" title="Autonomous Architectures" description="We architect systems that don't just follow static rules—they think, solve, and evolve." />
      </Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem' }}>
        {services.map((s, i) => (
          <Reveal key={i}>
            <section className="glass-card">
              <div style={{ color: BRAND.colors.violet, marginBottom: '1rem', fontWeight: '900', fontSize: '0.8rem', letterSpacing: '0.3em' }}>{s.label}</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>{s.title}</h3>
              <p style={{ color: BRAND.colors.slate500, lineHeight: 1.7, marginBottom: '1.5rem', flex: 1 }}>{s.desc}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '1.5rem' }}>
                {s.features.map((f, idx) => <span key={idx} style={{ padding: '6px 12px', background: BRAND.colors.slate50, border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', color: BRAND.colors.slate700 }}>{f}</span>)}
              </div>
              <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: BRAND.colors.slate400, borderTop: `1px solid ${BRAND.colors.slate100}`, paddingTop: '1rem', letterSpacing: '0.05em' }}>STACK: {s.tech}</div>
            </section>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

const MarketingPage = () => {
  const strategies = [
    { label: "ADS", title: "AI Meta Optimization", desc: "Predictive scaling models that maximize ROAS by targeting ultra-specific intent clusters.", features: ["Dynamic Creatives", "LTV Prediction", "Pixel 2.0"] },
    { label: "LEADS", title: "Outbound Synthesis", desc: "Automated cold-outreach systems that warm up domains and qualify leads before you wake up.", features: ["Apollo Intelligence", "Smart Sequences", "Lead Scoring"] },
    { label: "SOCIAL", title: "Autonomous Presence", desc: "Content cycles that manage distribution and engagement across every social touchpoint simultaneously.", features: ["Viral Prediction", "Auto-Engagement", "Metric Sync"] },
    { label: "CONTENT", title: "Semantic Engine", desc: "Generating expert-level, brand-synced long-form content that dominates SEO rankings naturally.", features: ["Voice Matching", "SEO Pillar Logic", "Zero-Prompt Ops"] }
  ];

  return (
    <div style={{ padding: '4rem 1.25rem 6rem', maxWidth: '1200px', margin: '0 auto' }}>
      <Reveal>
        <SectionHeader subtitle="Digital Marketing" title="Exponential Scaling" description="Using AI to remove the guesswork from growth and turn marketing into a predictable machine." />
      </Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem' }}>
        {strategies.map((s, i) => (
          <Reveal key={i}>
            <section className="glass-card" style={{ borderLeft: `6px solid ${BRAND.colors.blue}` }}>
              <div style={{ color: BRAND.colors.blue, marginBottom: '1rem', fontWeight: '900', fontSize: '0.8rem', letterSpacing: '0.3em' }}>{s.label}</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>{s.title}</h3>
              <p style={{ color: BRAND.colors.slate500, lineHeight: 1.7, marginBottom: '1.5rem', flex: 1 }}>{s.desc}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {s.features.map((f, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.95rem', color: BRAND.colors.slate700 }}>
                    <span style={{ color: BRAND.colors.blue, fontWeight: 'bold' }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

const Footer = ({ onNavigate }) => (
  <footer style={{ background: BRAND.colors.slate900, color: 'white', padding: '6rem 1.5rem 3rem', position: 'relative', zIndex: 10 }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '4rem', marginBottom: '4rem' }}>
        <div>
          <HeaderLogo isFooter={true} />
          <p style={{ color: BRAND.colors.slate400, lineHeight: 1.8, margin: '2rem 0', fontSize: '1rem' }}>
            Agenzyai is an AI-first architecture firm helping modern brands transition from manual operations to the era of autonomous scaling.
          </p>
        </div>
        <div>
          <h4 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '2rem', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Solutions</h4>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontSize: '1rem' }}>
            <button onClick={() => onNavigate('automation')} style={{ background: 'none', border: 'none', color: '#94A3B8', textAlign: 'left', cursor: 'pointer' }}>» Multi-Agent Systems</button>
            <button onClick={() => onNavigate('automation')} style={{ background: 'none', border: 'none', color: '#94A3B8', textAlign: 'left', cursor: 'pointer' }}>» Neural Voice Agents</button>
            <button onClick={() => onNavigate('marketing')} style={{ background: 'none', border: 'none', color: '#94A3B8', textAlign: 'left', cursor: 'pointer' }}>» Predictive Ad Scaling</button>
          </nav>
        </div>
        <div>
          <h4 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '2rem', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Connect</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontSize: '1rem' }}>
            <a href={`mailto:${BRAND.email}`} style={{ textDecoration: 'none', color: '#94A3B8' }}>{BRAND.email}</a>
            <a href={`https://wa.me/${BRAND.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: BRAND.colors.violet, fontWeight: 'bold' }}>WHATSAPP ARCHITECT</a>
          </div>
        </div>
      </div>
      <div style={{ borderTop: `1px solid rgba(255,255,255,0.05)`, paddingTop: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem', fontSize: '0.8rem', color: '#475569' }}>
        <p>© {new Date().getFullYear()} {BRAND.name}. All Rights Reserved. Expert AI Operations.</p>
      </div>
    </div>
  </footer>
);

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [showUnderDev, setShowUnderDev] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = `${currentPage.charAt(0).toUpperCase() + currentPage.slice(1)} | ${BRAND.name}`;
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === 'home') {
      const timer = setTimeout(() => setShowUnderDev(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  }, [currentPage]);

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'automation', label: 'Automation' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'about', label: 'About' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <div className="font-roboto" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <style>{customStyles}</style>
      <MagneticCursor />
      
      {/* Background Layer */}
      <div className="bg-canvas">
        <div className="aura-blob" />
        <div className="aura-blob aura-blob-2" />
        <div className="grid-bg" />
      </div>

      <header style={{ 
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 1000, 
        height: '100px', 
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(15px)',
        borderBottom: `1px solid rgba(226, 232, 240, 0.5)`,
        padding: '0 2rem',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div style={{ maxWidth: '1400px', width: '100%', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ cursor: 'pointer' }} onClick={() => setCurrentPage('home')}>
            <HeaderLogo floating={true} />
          </div>
          
          <nav className="desktop-nav" style={{ display: 'none', gap: '3rem' }}>
             <style>{`@media (min-width: 1024px) { .desktop-nav { display: flex !important; } .mobile-toggle { display: none !important; } }`}</style>
            {navLinks.map(pg => (
              <button 
                key={pg.id} 
                onClick={() => setCurrentPage(pg.id)} 
                className={`nav-btn ${currentPage === pg.id ? 'active' : ''}`}
              >
                {pg.label}
              </button>
            ))}
          </nav>

          <button className="mobile-toggle" onClick={() => setIsMobileMenuOpen(true)} style={{ background: 'none', border: 'none', fontSize: '28px', color: '#000' }}>☰</button>
        </div>
      </header>

      <div className={`overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={() => setIsMobileMenuOpen(false)} 
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(5px)', zIndex: 1001, opacity: isMobileMenuOpen ? 1 : 0, pointerEvents: isMobileMenuOpen ? 'auto' : 'none', transition: '0.3s' }} 
      />
      <nav className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`} 
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '300px', background: '#fff', zIndex: 1002, padding: '3rem', transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)', display: 'flex', flexDirection: 'column', gap: '1.5rem', boxShadow: '-20px 0 50px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
          <HeaderLogo isFooter={true} />
          <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: 'none', border: 'none', fontSize: '32px' }}>×</button>
        </div>
        {navLinks.map(pg => (
          <button 
            key={pg.id} 
            onClick={() => setCurrentPage(pg.id)} 
            style={{ 
              background: 'none', border: 'none', textTransform: 'uppercase', 
              fontSize: '15px', fontWeight: '800', textAlign: 'left',
              color: currentPage === pg.id ? BRAND.colors.violet : BRAND.colors.slate900,
              padding: '15px 0', borderBottom: `1px solid #f1f5f9`
            }}
          >
            {pg.label}
          </button>
        ))}
      </nav>

      <main style={{ flex: 1, paddingTop: '100px' }}>
        {currentPage === 'home' && <HomePage onNavigate={setCurrentPage} />}
        {currentPage === 'automation' && <AutomationPage />}
        {currentPage === 'marketing' && <MarketingPage />}
        
        {currentPage === 'contact' && (
          <article style={{ padding: '4rem 1.25rem 6rem', textAlign: 'center', position: 'relative', zIndex: 10 }}>
            <Reveal>
              <SectionHeader subtitle="Get In Touch" title="Initiate Deployment" description="Consult with our lead architects to identify high-impact automation opportunities." />
            </Reveal>
            <Reveal><ContactSection /></Reveal>
          </article>
        )}

        {currentPage === 'about' && (
          <article style={{ padding: '4rem 1.25rem 6rem', position: 'relative', zIndex: 10 }}>
            <Reveal><SectionHeader subtitle="Our Ecosystem" title="The Agenzyai Standard" description="We are more than a vendor—we are your long-term infrastructure partners." /></Reveal>
            <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3rem' }}>
              {[
                { title: "Strategic Precision", desc: "Every agent we deploy is built to solve a specific revenue-linked challenge.", icon: "🎯" },
                { title: "Radical Transparency", desc: "You own your architecture. No hidden prompts, no vendor lock-in.", icon: "💎" },
                { title: "Vault-Grade Security", desc: "We implement PII masking and local RAG to keep your trade secrets safe.", icon: "🔒" }
              ].map((item, idx) => (
                <Reveal key={idx}>
                  <div className="glass-card" style={{ textAlign: 'center', padding: '3.5rem 2rem' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>{item.icon}</div>
                    <h4 style={{ fontWeight: 'bold', fontSize: '1.4rem', marginBottom: '1rem' }}>{item.title}</h4>
                    <p style={{ fontSize: '1rem', color: BRAND.colors.slate500, lineHeight: 1.6 }}>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </article>
        )}
      </main>

      {showUnderDev && (
        <div 
          style={{ 
            position: 'fixed', bottom: '2rem', left: '2rem', 
            zIndex: 1500, background: 'rgba(15, 23, 42, 0.98)', color: 'white',
            padding: '1.2rem 2rem', borderRadius: '1.5rem', boxShadow: '0 30px 60px rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', gap: '1.5rem', border: `1.5px solid ${BRAND.colors.violet}`,
            maxWidth: '400px', backdropFilter: 'blur(20px)',
            animation: 'fadeIn 0.8s ease-out'
          }}
        >
          <div style={{ background: BRAND.colors.violet, width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🚧</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>Active Development</div>
            <div style={{ color: '#94A3B8', fontSize: '0.8rem' }}>Deploying new AI nodes. Core systems stable.</div>
          </div>
          <button onClick={() => setShowUnderDev(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '28px', opacity: 0.5 }}>×</button>
        </div>
      )}

      <Footer onNavigate={setCurrentPage} />

      {/* WHATSAPP WIDGET */}
      <a href={`https://wa.me/${BRAND.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="whatsapp-container">
        <div className="whatsapp-label">
          Chat with an Expert
        </div>
        <div className="whatsapp-btn">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-7.6 8.38 8.38 0 0 1 3.8.9L22 4l-2.5 5.5Z" fill="white" stroke="none"/>
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-7.6 8.38 8.38 0 0 1 3.8.9L22 4l-2.5 5.5Z"/>
          </svg>
        </div>
      </a>
    </div>
  );
};

export default App;